/**
 * Web UI App — supports two modes:
 * 1. Same-origin: fetches WS config from /api/config (cookie auth, traditional deployment)
 * 2. Remote: user provides a tunnel URL + token (Cloudflare Pages deployment)
 *
 * In remote mode, the user enters their tunnel URL and server token,
 * and we connect directly via WebSocket with bearer token auth.
 */

import React, { useState, useEffect, useRef, lazy, Suspense, useCallback, useMemo } from 'react'
import { createWebApi } from './adapter/web-api'
import type { WsRpcClient } from '../../electron/src/transport/client'

const ElectronApp = lazy(() => import('@/App'))

type Phase = 'connect' | 'loading' | 'error' | 'ready'

// ---------------------------------------------------------------------------
// i18n for connect/error screens
// ---------------------------------------------------------------------------

type Locale = 'en' | 'zh'

function detectLocale(): Locale {
  const lang = navigator.language?.toLowerCase() ?? ''
  return lang.startsWith('zh') ? 'zh' : 'en'
}

const i18nStrings: Record<string, Record<Locale, string>> = {
  'app.title': { en: 'XCLAW', zh: 'XCLAW' },
  'app.subtitle': { en: 'Connect to your XClaw server', zh: '连接到你的 XClaw 服务器' },
  'connect.serverUrl': { en: 'Server URL', zh: '服务器地址' },
  'connect.serverUrlPlaceholder': { en: 'https://xxx.trycloudflare.com', zh: 'https://xxx.trycloudflare.com' },
  'connect.token': { en: 'Token', zh: '令牌' },
  'connect.tokenPlaceholder': { en: 'Server token from app settings', zh: '应用设置中的服务器令牌' },
  'connect.button': { en: 'Connect', zh: '连接' },
  'connect.hint': { en: 'Open your XClaw desktop app, go to Settings → Server, enable remote access to get the URL and token.', zh: '打开 XClaw 桌面应用，进入 设置 → 服务器，启用远程访问获取地址和令牌。' },
  'connect.urlRequired': { en: 'Please enter a server URL', zh: '请输入服务器地址' },
  'connect.tokenRequired': { en: 'Please enter the server token', zh: '请输入服务器令牌' },
  'loading.text': { en: 'Connecting to server...', zh: '正在连接服务器...' },
  'error.title': { en: 'Connection Failed', zh: '连接失败' },
  'error.retry': { en: 'Retry', zh: '重试' },
  'error.changeServer': { en: 'Change Server', zh: '更换服务器' },
}

function tt(key: string, locale: Locale): string {
  return i18nStrings[key]?.[locale] ?? key
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'xclaw-remote-connection'

interface SavedConnection {
  serverUrl: string
  token: string
}

function isInvalidTokenError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return error.message === 'Invalid token' || (error as { code?: string }).code === 'AUTH_FAILED'
}

function loadSavedConnection(): SavedConnection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function saveConnection(conn: SavedConnection): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(conn))
}

function clearSavedConnection(): void {
  localStorage.removeItem(STORAGE_KEY)
}

// ---------------------------------------------------------------------------
// Detect mode: same-origin server or remote (Cloudflare Pages)
// ---------------------------------------------------------------------------

function isSameOriginMode(): boolean {
  // If we can reach /api/config on the same origin, we're in same-origin mode
  // For Cloudflare Pages deployment, there's no /api/config endpoint
  return !!window.location.pathname.match(/^\/login/) || document.cookie.includes('xclaw_session')
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

function LoadingScreen({ locale }: { locale: Locale }) {
  return (
    <div className="flex flex-col items-center justify-center h-screen font-sans text-foreground/50 gap-3">
      <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full" />
      <p className="text-[13px]">{tt('loading.text', locale)}</p>
    </div>
  )
}

function ErrorScreen({ message, onRetry, onDisconnect, locale }: { message: string; onRetry: () => void; onDisconnect: () => void; locale: Locale }) {
  return (
    <div className="flex flex-col items-center justify-center h-screen font-sans text-foreground/50 gap-3">
      <p className="text-base font-medium text-destructive">{tt('error.title', locale)}</p>
      <p className="text-[13px] max-w-md text-center">{message}</p>
      <div className="flex gap-2 mt-2">
        <button
          onClick={onRetry}
          className="px-4 py-1.5 rounded-md bg-background shadow-minimal text-[13px] text-foreground/70 cursor-pointer"
        >
          {tt('error.retry', locale)}
        </button>
        <button
          onClick={onDisconnect}
          className="px-4 py-1.5 rounded-md bg-background shadow-minimal text-[13px] text-foreground/70 cursor-pointer"
        >
          {tt('error.changeServer', locale)}
        </button>
      </div>
    </div>
  )
}

function ConnectScreen({ onConnect, initialValues, locale }: {
  onConnect: (serverUrl: string, token: string) => void
  initialValues?: SavedConnection | null
  locale: Locale
}) {
  const [serverUrl, setServerUrl] = useState(initialValues?.serverUrl ?? '')
  const [token, setToken] = useState(initialValues?.token ?? '')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    let url = serverUrl.trim()
    if (!url) {
      setError(tt('connect.urlRequired', locale))
      return
    }

    // Normalize URL: accept https:// and convert to wss://
    if (url.startsWith('https://')) {
      url = url.replace('https://', 'wss://')
    } else if (url.startsWith('http://')) {
      url = url.replace('http://', 'ws://')
    } else if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
      url = `wss://${url}`
    }

    if (!token.trim()) {
      setError(tt('connect.tokenRequired', locale))
      return
    }

    onConnect(url, token.trim())
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen font-sans gap-6 px-4">
      <div className="text-center mb-2">
        <h1 className="text-2xl font-bold text-foreground tracking-wide">{tt('app.title', locale)}</h1>
        <p className="text-[13px] text-foreground/50 mt-1">{tt('app.subtitle', locale)}</p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <div>
          <label className="block text-[13px] font-medium text-foreground/70 mb-1.5">
            {tt('connect.serverUrl', locale)}
          </label>
          <input
            type="text"
            value={serverUrl}
            onChange={e => setServerUrl(e.target.value)}
            placeholder={tt('connect.serverUrlPlaceholder', locale)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/30"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-[13px] font-medium text-foreground/70 mb-1.5">
            {tt('connect.token', locale)}
          </label>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder={tt('connect.tokenPlaceholder', locale)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}

        <button
          type="submit"
          className="w-full py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
        >
          {tt('connect.button', locale)}
        </button>
      </form>

      <p className="text-[11px] text-foreground/30 max-w-xs text-center">
        {tt('connect.hint', locale)}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------

export default function App() {
  const [phase, setPhase] = useState<Phase>('loading')
  const [error, setError] = useState('')
  const clientRef = useRef<WsRpcClient | null>(null)
  const initRef = useRef(false)
  const locale = useMemo(() => detectLocale(), [])

  // Try same-origin first, then check saved connection, then show connect screen
  const initialize = useCallback(async () => {
    setPhase('loading')
    setError('')

    // Try same-origin /api/config first (traditional server deployment)
    try {
      const configRes = await fetch('/api/config', { credentials: 'same-origin' })
      if (configRes.ok) {
        const { wsUrl } = await configRes.json() as { wsUrl: string }
        if (wsUrl) {
          // Same-origin mode — proceed like before
          let workspaceId: string | undefined
          const params = new URLSearchParams(window.location.search)
          workspaceId = params.get('workspace') ?? undefined

          if (!workspaceId) {
            try {
              const wsRes = await fetch('/api/config/workspaces', { credentials: 'same-origin' })
              if (wsRes.ok) {
                const { defaultWorkspaceId } = await wsRes.json() as { defaultWorkspaceId?: string }
                if (defaultWorkspaceId) workspaceId = defaultWorkspaceId
              }
            } catch {}
          }

          connectToServer(wsUrl, undefined, workspaceId)
          return
        }
      } else if (configRes.status === 401) {
        // Has a server but not authenticated — redirect to login
        window.location.href = '/login'
        return
      }
    } catch {
      // No same-origin server available — this is expected on Cloudflare Pages
    }

    // Check saved remote connection
    const saved = loadSavedConnection()
    if (saved) {
      connectToServer(saved.serverUrl, saved.token)
      return
    }

    // No connection available — show connect screen
    setPhase('connect')
  }, [])

  const connectToServer = useCallback(async (serverUrl: string, token?: string, workspaceId?: string) => {
    setPhase('loading')
    setError('')

    try {
      if (clientRef.current) {
        clientRef.current.destroy()
      }

      const { api, client } = createWebApi({ serverUrl, workspaceId, token })
      clientRef.current = client

      ;(window as any).electronAPI = api
      client.connect()

      // Force one RPC round-trip before rendering the shared app so auth
      // failures surface here instead of showing a half-ready UI.
      await api.getSetupNeeds()

      // For remote connections without a workspace ID, wait for connection
      // then fetch workspaces and switch to the first one
      if (!workspaceId && token) {
        const workspaces = await api.getWorkspaces()
        if (workspaces && workspaces.length > 0) {
          const firstWs = workspaces[0]
          await api.switchWorkspace(firstWs.id)
        }
      }

      // Save remote connection only after the handshake/auth preflight succeeds.
      if (token) {
        saveConnection({ serverUrl, token })
      }

      setPhase('ready')
    } catch (err) {
      if (isInvalidTokenError(err)) {
        clearSavedConnection()
      }
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      setPhase('error')
    }
  }, [])

  const handleDisconnect = useCallback(() => {
    clientRef.current?.destroy()
    clientRef.current = null
    clearSavedConnection()
    setPhase('connect')
  }, [])

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true
      initialize()
    }
    return () => { clientRef.current?.destroy() }
  }, [])

  if (phase === 'connect') {
    return <ConnectScreen onConnect={connectToServer} initialValues={loadSavedConnection()} locale={locale} />
  }
  if (phase === 'loading') return <LoadingScreen locale={locale} />
  if (phase === 'error') {
    return <ErrorScreen message={error} onRetry={initialize} onDisconnect={handleDisconnect} locale={locale} />
  }

  return (
    <Suspense fallback={<LoadingScreen locale={locale} />}>
      <ElectronApp />
    </Suspense>
  )
}
